import React from "react";
import { useTranslation } from "./useTranslation";

const TermsOfService = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t("tos.title")}
          </h1>
          <p className="text-lg text-gray-700">{t("tos.subtitle")}</p>
        </div>

        {/* Last Updated */}
        <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 mb-8">
          <p className="text-sm text-gray-800">
            <strong>{t("tos.lastUpdated.label")}</strong> {t("tos.lastUpdated.date")}
          </p>
        </div>

        <div className="prose prose-lg max-w-none text-gray-800">
          {/* Agreement */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("tos.sections.agreement.heading")}
            </h2>
            <p className="mb-4 leading-relaxed">{t("tos.sections.agreement.p1")}</p>
            <p className="leading-relaxed">{t("tos.sections.agreement.p2")}</p>
          </section>

          {/* Service Description */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("tos.sections.service.heading")}
            </h2>
            <p className="mb-4 leading-relaxed">{t("tos.sections.service.p1")}</p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>{t("tos.sections.service.items.0")}</li>
              <li>{t("tos.sections.service.items.1")}</li>
              <li>{t("tos.sections.service.items.2")}</li>
              <li>{t("tos.sections.service.items.3")}</li>
              <li>{t("tos.sections.service.items.4")}</li>
            </ul>
            <p className="text-sm text-gray-700">{t("tos.sections.service.notice")}</p>
          </section>

          {/* Eligibility */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t("tos.sections.eligibility.heading")}</h2>
            <p className="mb-4">{t("tos.sections.eligibility.p1")}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t("tos.sections.eligibility.items.0")}</li>
              <li>{t("tos.sections.eligibility.items.1")}</li>
              <li>{t("tos.sections.eligibility.items.2")}</li>
              <li>{t("tos.sections.eligibility.items.3")}</li>
              <li>{t("tos.sections.eligibility.items.4")}</li>
            </ul>
          </section>

          {/* Account Registration */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("tos.sections.account.heading")}
            </h2>
            <p className="mb-4">{t("tos.sections.account.p1")}</p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold mb-2">{t("tos.sections.account.registered.title")}</h3>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>{t("tos.sections.account.registered.items.0")}</li>
                  <li>{t("tos.sections.account.registered.items.1")}</li>
                  <li>{t("tos.sections.account.registered.items.2")}</li>
                  <li>{t("tos.sections.account.registered.items.3")}</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold mb-2">{t("tos.sections.account.guest.title")}</h3>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>{t("tos.sections.account.guest.items.0")}</li>
                  <li>{t("tos.sections.account.guest.items.1")}</li>
                  <li>{t("tos.sections.account.guest.items.2")}</li>
                  <li>{t("tos.sections.account.guest.items.3")}</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Payment Terms */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("tos.sections.payment.heading")}
            </h2>
            <p className="mb-4">{t("tos.sections.payment.p1")}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t("tos.sections.payment.items.0")}</li>
              <li>{t("tos.sections.payment.items.1")}</li>
              <li>{t("tos.sections.payment.items.2")}</li>
              <li>{t("tos.sections.payment.items.3")}</li>
            </ul>
          </section>

          {/* Refund Policy */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("tos.sections.refunds.heading")}
            </h2>
            <p className="mb-4">{t("tos.sections.refunds.p1")}</p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>{t("tos.sections.refunds.items.0")}</li>
              <li>{t("tos.sections.refunds.items.1")}</li>
              <li>{t("tos.sections.refunds.items.2")}</li>
              <li>{t("tos.sections.refunds.items.3")}</li>
            </ul>
            <p>
              {t("tos.sections.refunds.p2.prefix")}{" "}
              <a href="mailto:support@routedlabs.com" className="text-blue-600 underline">
                support@routedlabs.com
              </a>
              {t("tos.sections.refunds.p2.suffix")}
            </p>
          </section>

          {/* Service Limitations */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("tos.sections.limitations.heading")}
            </h2>
            <p className="mb-4">{t("tos.sections.limitations.p1")}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t("tos.sections.limitations.items.0")}</li>
              <li>{t("tos.sections.limitations.items.1")}</li>
              <li>{t("tos.sections.limitations.items.2")}</li>
              <li>{t("tos.sections.limitations.items.3")}</li>
              <li>{t("tos.sections.limitations.items.4")}</li>
            </ul>
          </section>

          {/* Prohibited Uses */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("tos.sections.prohibited.heading")}
            </h2>
            <p className="mb-4">{t("tos.sections.prohibited.p1")}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t("tos.sections.prohibited.items.0")}</li>
              <li>{t("tos.sections.prohibited.items.1")}</li>
              <li>{t("tos.sections.prohibited.items.2")}</li>
              <li>{t("tos.sections.prohibited.items.3")}</li>
              <li>{t("tos.sections.prohibited.items.4")}</li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("tos.sections.liability.heading")}
            </h2>
            <p className="mb-4">{t("tos.sections.liability.p1")}</p>
          </section>

          {/* Indemnification */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("tos.sections.indemnity.heading")}
            </h2>
            <p>{t("tos.sections.indemnity.p1")}</p>
          </section>

          {/* Termination */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("tos.sections.termination.heading")}
            </h2>
            <p>{t("tos.sections.termination.p1")}</p>
          </section>

          {/* Governing Law */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("tos.sections.law.heading")}
            </h2>
            <p>{t("tos.sections.law.p1")}</p>
          </section>

          {/* Amendments */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("tos.sections.amend.heading")}
            </h2>
            <p>{t("tos.sections.amend.p1")}</p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("tos.sections.contact.heading")}
            </h2>
            <p className="mb-4">{t("tos.sections.contact.p1")}</p>
            <p>
              <a href="mailto:support@routedlabs.com" className="text-blue-600 underline">
                support@routedlabs.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
